import React from 'react'
import { createResource, useResource } from '@/lib/i18n'
import styled from 'styled-components'
import { Animation, Reveal } from 'react-genie'
import Head from 'next/head'
import dynamic from 'next/dynamic'

const Footer = dynamic(() => import('@/components/home/footer'))
const KPHead = dynamic(() => import('@/components/head'))
const TopNavigation = dynamic(() => import('@/components/top-navigation'))

const resource = createResource({
  en: {
    contentTitle: 'Kredit Pintar Disclaimer',
    description:
      'All services are provided following the applicable information security policy and set by Kredit Pintar, a money loan service provider.',
    contentSubTitle:
      'As part of our commitment to maintaining information security, we implement the following measures:',
    section1:
      'Implementing an Information Security Management System (“<b>ISMS</b>”) based on the ISO/IEC 27001 standard.',
    section2:
      'Continuously seeking to comply with all applicable laws and regulations, including those relating to personal data protection, information security, and LPBBTI.',
    section3:
      'Conducting continuous monitoring, evaluation, and improvement of our ISMS to maintain the confidentiality, integrity, and availability of information.',
    section4:
      'Although we have implemented reasonable security measures in accordance with industry standards and applicable laws and regulations, we cannot guarantee that the LPBBTI services provided by us will be completely free from disruptions, system failures, cyberattacks, or force majeure events beyond our control.',
    section5:
      'TKB90 is a measure of the success rate of an LPBBTI organizer in facilitating the settlement of funding obligations within a period of up to 90 (ninety) days from the relevant due date. The TKB90 value does not constitute a guarantee of the success of any future funding and should not be used as a basis for estimating the return on any particular funding.'
  },
  id: {
    contentTitle: 'Kredit Pintar Disclaimer',
    description:
      'Seluruh layanan diberikan mengikuti kebijakan keamanan informasi yang berlaku dan ditetapkan oleh Kredit Pintar, penyedia layanan pinjaman uang.',
    contentSubTitle:
      'Sebagai bentuk komitmen kami dalam menjaga keamanan informasi, kami menerapkan langkah-langkah sebagai berikut:',
    section1:
      'Menerapkan Sistem Manajemen Keamanan Informasi (<i>Information Security Management System</i>) berdasarkan standar ISO/IEC 27001.',
    section2:
      'Senantiasa berupaya untuk mematuhi segala ketentuan peraturan perundang-undangan yang berlaku, termasuk ketentuan mengenai perlindungan data pribadi, keamanan informasi, dan LPBBTI.',
    section3: 
      'Melaksanakan pemantauan, evaluasi, dan perbaikan secara berkelanjutan terhadap Sistem Manajemen Keamanan Informasi guna menjaga kerahasiaan, integritas, dan ketersediaan informasi.',
    section4:
      'Meskipun kami telah menerapkan langkah-langkah pengamanan yang wajar sesuai dengan standar industri dan ketentuan peraturan perundang-undangan yang berlaku, kami tidak dapat menjamin bahwa LPBBTI yang kami sediakan akan sepenuhnya bebas dari gangguan, kegagalan sistem, serangan siber, atau keadaan kahar (<i>force majeure</i>) yang berada di luar kendali kami.',
    section5:
      'TKB90 adalah tingkat keberhasilan penyelenggara LPBBTI dalam memfasilitasi penyelesaian kewajiban pendanaan sampai dengan 90 (sembilan puluh) hari sejak tanggal jatuh tempo. Nilai TKB90 bukan merupakan jaminan atas keberhasilan pendanaan di masa mendatang dan tidak dapat dijadikan dasar untuk memperkirakan tingkat pengembalian atas suatu pendanaan.'
  },
})

const Disclaimer = () => {
  const { t } = useResource(resource)
  return (
    <DisclaimerStyle>
      <Reveal animation={Animation.FadeIn}>
        <KPHead metaTitle="disclaimer" />
        <Head>
          <meta name="description" content={t('description')} />
        </Head>
        <TopNavigation />
        <div className="disclaimer">
          <h1 className="title">{t('contentTitle')}</h1>
          <div className="content">
            <div>
              <div>{t('contentSubTitle')}</div>
              <div dangerouslySetInnerHTML={{ __html: `<span>1. </span>${t('section1')}` }} />
              <div>
                <span>2. </span>
                {t('section2')}
              </div>
              <div>
                <span>3. </span>
                {t('section3')}
              </div>
              <div dangerouslySetInnerHTML={{ __html: `<span>4. </span>${t('section4')}` }} />
              <div>
                <span>5. </span>
                {t('section5')}
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </Reveal>
    </DisclaimerStyle>
  )
}

export default Disclaimer

const DisclaimerStyle = styled.div`
  width: 100%;
  min-height: 30vh;
  @media only screen and (max-width: 1000px) {
    .disclaimer {
      margin: 0 auto;
      padding: 78px 24px;
      .title {
        margin: 32px 0 24px;
        text-align: center;
        font-size: 24px;
        font-family: Roboto-Medium, Roboto;
        font-weight: 500;
        color: rgba(0, 0, 0, 0.87);
        line-height: 28px;
      }
      .content {
        display: block !important;
        font-size: 16px;
        font-family: Roboto-Regular, Roboto;
        font-weight: 400;
        color: #757575;
        line-height: 24px;
        div {
          padding-bottom: 12px;
          span {
            color: #212121;
            font-family: Roboto-Bold, Roboto;
          }
        }
        b {
            color: #212121;
            font-family: Roboto-Bold, Roboto;
            font-weight: 700;
          }
      }
    }
  }
  @media only screen and (min-width: 1000px) {
    .disclaimer {
      max-width: 1140px;
      margin: 0 auto;
      padding: 82px 0;
      border-bottom: 1px solid #eee;
      .title {
        margin: 80px 0 40px;
        text-align: center;
        font-size: 36px;
        font-family: Roboto-Medium, Roboto;
        font-weight: 500;
        color: rgba(0, 0, 0, 0.87);
        line-height: 48px;
      }
      .content {
        display: flex;
        justify-content: space-between;
        font-size: 16px;
        font-family: Roboto-Regular, Roboto;
        font-weight: 400;
        color: #757575;
        line-height: 24px;
        div {
          padding-bottom: 20px;
          span {
            color: #212121;
            font-family: Roboto-Bold, Roboto;
          }
          b {
            color: #212121;
            font-family: Roboto-Bold, Roboto;
            font-weight: 700;
          }
        }
      }
    }
  }

  .three {
    padding: 0;
    border: 0;
  }
`
